import { HeartPulse } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseMetricLogCard } from "@/core/components/tracking";
import { updateAnxietyLevel } from "@/lib/api";
import { useProductContext } from "@/contexts/ProductContext";

interface AnxietyLogCardProps {
  onComplete?: () => void;
  onDataUpdate?: () => void;
  isLoading?: boolean;
}

export function AnxietyLogCard({ onComplete, onDataUpdate, isLoading }: AnxietyLogCardProps) {
  const { t } = useTranslation();
  const { currentProduct } = useProductContext();

  return (
    <BaseMetricLogCard
      // Display
      icon={HeartPulse}
      gradientClasses="from-blue-500 to-cyan-600"
      borderHoverClasses="hover:border-blue-500/40"

      // Input type - using slider for 1-10 scale
      inputType="slider"
      sliderConfig={{
        min: 1,
        max: 10,
        step: 1,
        defaultValue: 5,
        showScale: true,
        scaleLabels: ["😌", "😊", "🙂", "😐", "😕", "😟", "😥", "😨", "😰", "😱"],
      }}

      // Data field
      fieldName="anxiety_level"

      // Validation
      minValue={1}
      maxValue={10}

      // Pre-translated strings
      cardTitle={t('mindco-relief:actions.logAnxiety', 'Log Anxiety')}
      cardDescription={t('mindco-relief:actions.anxietyHelp', 'Rate your current anxiety level')}
      dialogTitle={t('mindco-relief:actions.logAnxiety', 'Log Anxiety')}
      dialogDescription={t('mindco-relief:actions.anxietyHelp', 'Rate your current anxiety level')}
      inputLabel={t('mindco-relief:actions.levelFor', 'Level for')}
      inputPlaceholder="1-10"
      scaleDescription={t('mindco-relief:actions.anxietyScale', '1 = Very calm, 10 = Extremely anxious')}
      notesPlaceholder={t('mindco-relief:actions.notesPlaceholder', 'Any notes about how you\'re feeling...')}
      loggedTitle={t('mindco-relief:actions.anxietyLogged', 'Anxiety Level Logged')}
      loggedDescription={t('mindco-relief:actions.anxietyLoggedDescription', 'Your anxiety level has been recorded.')}
      errorInvalidInput={t('common:errors.invalidInput', 'Invalid Input')}
      errorInvalidValue={t('mindco-relief:errors.invalidAnxietyLevel', 'Please enter a valid anxiety level (1-10).')}

      // Analytics
      analyticsEvent="Anxiety Level Logged"

      // Popup triggers
      popupTriggers={[
        { trigger: "anxiety_logged" },
        {
          trigger: "high_anxiety_logged",
          condition: (value) => value >= 7,
          contextMapper: (value) => ({ level: value })
        }
      ]}

      // API function
      updateFunction={updateAnxietyLevel}

      // Callbacks
      onComplete={onComplete}
      onDataUpdate={onDataUpdate}
      isLoading={isLoading}

      // Product context
      productId={currentProduct}
    />
  );
}
